import type { NewsletterSection, SectionType } from "../lib/segment-types";
import { SECTION_LABEL } from "../lib/sections";
import { SectionIcon } from "./SectionIcon";
import { IconArrowDown, IconArrowUp, IconTrash } from "./icons";

/**
 * The block library as a grid of tiles: click one to add it, or drag it into
 * the email and drop it where the line shows.
 *
 * Only blocks this renderer actually draws are offered. A tile for video, a
 * survey or a product card would add something that never reaches the inbox.
 */
export const PALETTE: { type: SectionType; label: string; layout?: NewsletterSection["layout"] }[] = [
  { type: "articles", label: "Link cards" },
  { type: "featured", label: "Featured link" },
  { type: "block", label: "Content block" },
  { type: "image", label: "Photo" },
  { type: "banner", label: "Banner" },
  { type: "cta", label: "Button" },
  { type: "text", label: "Text" },
  { type: "tip", label: "Health tip" },
  { type: "doctor", label: "Doctor note" },
  { type: "category", label: "Heading" },
  { type: "divider", label: "Divider" },
  { type: "spacer", label: "Spacer" },
];

export function BlockPalette({
  sections,
  locked,
  onAdd,
  onDragStart,
  onDragEnd,
  onSelect,
  onMove,
  onRemove,
}: {
  sections: NewsletterSection[];
  locked: boolean;
  onAdd: (type: SectionType, layout?: NewsletterSection["layout"]) => void;
  onDragStart: (type: SectionType, layout?: NewsletterSection["layout"]) => void;
  onDragEnd: () => void;
  onSelect: (id: string) => void;
  onMove: (id: string, delta: -1 | 1) => void;
  onRemove: (id: string) => void;
}) {
  const listed = sections.filter((s) => s.type !== "footer");

  return (
    <div className="nl-palette">
      <h2 className="nl-panel-title">Add blocks</h2>
      <p className="nl-panel-sub">Click to add, or drag into the email.</p>

      <div className="nl-tiles" role="list">
        {PALETTE.map((item) => (
          <button
            key={item.type + item.label}
            type="button"
            role="listitem"
            className="nl-tile"
            disabled={locked}
            title={locked ? "This newsletter is locked for editing" : `Add ${item.label.toLowerCase()}`}
            draggable={!locked}
            onClick={() => onAdd(item.type, item.layout)}
            onDragStart={(e) => {
              e.dataTransfer.effectAllowed = "copy";
              e.dataTransfer.setData("text/plain", item.type);
              onDragStart(item.type, item.layout);
            }}
            onDragEnd={onDragEnd}
          >
            <SectionIcon type={item.type} size={28} />
            <span className="nl-tile-label">{item.label}</span>
            <span className="nl-tile-grip" aria-hidden />
          </button>
        ))}
      </div>

      {/* The canvas is clicked with a pointer; this is the same choice by
          keyboard, and the one place blocks are reordered. */}
      <h2 className="nl-panel-title nl-panel-title-gap">In this email</h2>
      {listed.length === 0 ? (
        <p className="nl-panel-sub">No blocks yet. Add one above.</p>
      ) : (
        <ol className="nl-outline">
          {listed.map((s, i) => {
            // Say what is in the block, not only its kind: three rows reading
            // "Featured link" could not be told apart.
            const type = SECTION_LABEL[s.type];
            const content = s.heading?.trim() || s.articles?.[0]?.title?.trim() || s.text?.trim().slice(0, 80) || "";
            const name = content || type;
            return (
              <li key={s.id} className="nl-outline-row">
                <button type="button" className="nl-outline-open" onClick={() => onSelect(s.id)}>
                  <SectionIcon type={s.type} size={18} />
                  <span className="nl-outline-text">
                    <bdi dir="auto">{name}</bdi>
                    {content && <small>{type}</small>}
                  </span>
                </button>
                {!locked && (
                  <span className="nl-outline-tools">
                    <button type="button" aria-label={`Move ${name} up`} title="Move up"
                      disabled={i === 0} onClick={() => onMove(s.id, -1)}><IconArrowUp /></button>
                    <button type="button" aria-label={`Move ${name} down`} title="Move down"
                      disabled={i === listed.length - 1} onClick={() => onMove(s.id, 1)}><IconArrowDown /></button>
                    <button type="button" className="is-danger" aria-label={`Remove ${name}`} title="Remove"
                      onClick={() => onRemove(s.id)}><IconTrash /></button>
                  </span>
                )}
              </li>
            );
          })}
        </ol>
      )}
    </div>
  );
}
